import { useState, useEffect, useRef } from "react";
import { totalFilterCount, updateFacetsData } from "../util/helper";

/**
 * Common filter/pagination state and handlers shared by Explore and Posts list pages.
 *
 * @param {number}   limit           - Page size
 * @param {Function} fetchFn         - (limit, page, term, filterOptions) => Promise<raw>
 * @param {Function} normalizeResult - (raw) => { records, total, facetsHeaders, facets, pageNumber }
 */
const defaultFilterOptions = {
  search: "",
  filters: {},
  gate: "AND",
  sortBy_v: "",
  sortOrder_v: "asc",
};

const readStorage = (key) => {
  try {
    return key ? JSON.parse(sessionStorage.getItem(key) || "null") : null;
  } catch {
    return null;
  }
};

export function useFilterPage({
  limit,
  fetchFn,
  normalizeResult,
  storageKey = null,
}) {
  const saved = readStorage(storageKey);

  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(saved?.currentPage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(saved?.searchText || "");
  const [facets, setFacets] = useState([]);
  const [apply, setApply] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [filteredResultCount, setFilteredResultCount] = useState(0);
  const [showFilter, setShowFilter] = useState(saved?.showFilter || false);
  const [filterPills, setFilterPills] = useState(saved?.filterPills || null);
  const [filterOptions, setFilterOptions] = useState(
    saved?.filterOptions || defaultFilterOptions,
  );
  const [sortByHeader, setSortByHeader] = useState([]);

  // Always-fresh refs so effects never close over stale state
  const filterOptionsRef = useRef(filterOptions);
  filterOptionsRef.current = filterOptions;
  const searchTextRef = useRef(searchText);
  searchTextRef.current = searchText;

  // Guards against out-of-order responses: page-change, search, clear, and
  // apply-filters can all trigger fetchData independently, so an earlier
  // in-flight request resolving after a later one must not overwrite it.
  const fetchId = useRef(0);

  const fetchData = async (page, term, activeFilterOptions) => {
    setLoading(true);
    const currentFetchId = ++fetchId.current;
    try {
      const raw = await fetchFn(limit, page, term, activeFilterOptions);
      if (fetchId.current !== currentFetchId) return;

      const {
        records: newRecords,
        total,
        facetsHeaders,
        facets: facetsRaw,
        pageNumber,
        sortByHeader: rawSortByHeader,
      } = normalizeResult(raw);

      setRecords(newRecords || []);
      setTotalPages(Math.ceil((total || 0) / limit));
      setTotalRecords(total || 0);
      setFilteredResultCount(total || 0);

      if (pageNumber && parseInt(pageNumber) !== page) {
        setCurrentPage(parseInt(pageNumber));
      }

      if (rawSortByHeader?.length > 0) {
        setSortByHeader(rawSortByHeader);
      }

      if (facetsHeaders && facetsRaw) {
        const formattedFacets = updateFacetsData({
          data: { facetsHeaders, facets: facetsRaw },
        });
        setFacets(formattedFacets);

        if (formattedFacets?.length > 0 && page === 1) {
          setFilterOptions((prev) => ({
            ...prev,
            filters: {
              ...formattedFacets.reduce((acc, facet) => {
                acc[facet.key] = [];
                return acc;
              }, {}),
              ...prev.filters,
            },
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (fetchId.current === currentFetchId) {
        setLoading(false);
      }
    }
  };

  const handleSearch = (term) => {
    if (term.trim()) {
      setSearchText(term);
      setCurrentPage(1);
      const updated = { ...filterOptions, search: term };
      setFilterOptions(updated);
      setFilterPills(updated);
      fetchData(1, term, updated);
    }
  };

  const handleClear = () => {
    setSearchText("");
    const updated = { ...filterOptions, search: "" };
    setFilterOptions(updated);
    setFilterPills(updated);
    fetchData(1, "", updated);
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(parseInt(page));
  };

  const handleCheckboxChange = (value, type) => {
    let selectedArray = [];
    let selectedString = "";

    switch (value) {
      case "sort":
        selectedString = filterOptions.sortBy_v;
        break;
      case "gate":
        selectedString = filterOptions.gate;
        break;
      case "searches":
        selectedString = filterOptions.search;
        break;
      default:
        if (value in filterOptions.filters)
          selectedArray = filterOptions.filters[value];
        break;
    }

    if (Array.isArray(type)) {
      // Bulk replace (used by FilterBody's "Select All" / clear-all toggle)
      // rather than the usual single-value toggle below.
      selectedArray = type;
    } else if (selectedArray.includes(type)) {
      selectedArray = selectedArray.filter((item) => item !== type);
    } else {
      selectedArray = [...selectedArray, type];
    }
    if (selectedString !== type) selectedString = type;

    switch (value) {
      case "sort":
        setFilterOptions((prev) => ({ ...prev, sortBy_v: selectedString }));
        break;
      case "gate":
        setFilterOptions((prev) => ({ ...prev, gate: selectedString }));
        break;
      case "searches":
        setFilterOptions((prev) => ({ ...prev, search: selectedString }));
        break;
      default:
        if (value in filterOptions.filters) {
          setFilterOptions((prev) => ({
            ...prev,
            filters: { ...prev.filters, [value]: selectedArray },
          }));
        }
        break;
    }
  };

  const handleReset = () => {
    setFilteredResultCount(0);
    const initializedFilters = facets.reduce((acc, facet) => {
      acc[facet.key] = [];
      return acc;
    }, {});
    const reset = {
      search: "",
      filters: initializedFilters,
      sortBy_v: "",
      gate: "AND",
      sortOrder_v: "asc",
    };
    setFilterOptions(reset);
    setFilterPills(reset);
    setFilterCount(0);
    setSearchText("");
    if (storageKey) sessionStorage.removeItem(storageKey);
  };

  useEffect(() => {
    if (!storageKey) return;
    sessionStorage.setItem(
      storageKey,
      JSON.stringify({
        currentPage,
        searchText,
        showFilter,
        filterOptions,
        filterPills,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchText, showFilter, filterOptions, filterPills]);

  useEffect(() => {
    fetchData(currentPage, searchText, filterOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    if (apply) {
      window.scrollTo(0, 0);
      setApply(false);
      fetchData(1, searchTextRef.current, filterOptionsRef.current);
      setFilterPills(filterOptionsRef.current);
    }
    setFilterCount(totalFilterCount(filterOptionsRef.current?.filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apply]);

  return {
    records,
    setRecords,
    currentPage,
    setCurrentPage,
    totalPages,
    totalRecords,
    loading,
    setLoading,
    searchText,
    setSearchText,
    facets,
    apply,
    setApply,
    filterCount,
    filteredResultCount,
    showFilter,
    setShowFilter,
    filterPills,
    setFilterPills,
    filterOptions,
    setFilterOptions,
    sortByHeader,
    fetchData,
    handleSearch,
    handleClear,
    handlePageChange,
    handleCheckboxChange,
    handleReset,
  };
}
